import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styles from './AuditionDetailPage.module.css';
import PageLayout from '../../components/layout/PageLayout/PageLayout';
import AuditionDetailBack from './AuditionDetailBack';
import AuditionDetailHero from './AuditionDetailHero';
import AuditionDetailTabs from './AuditionDetailTabs';
import AuditionDetailContent from './AuditionDetailContent';
import AuditionDetailSidebar from './AuditionDetailSidebar';
import ApplyModal from './ApplyModal';
import LoadingSpinner from '../../shared/components/LoadingSpinner';
import RoleDetailModal from './RoleDetailModal';
import useAuditionDetail from '../../hooks/useAuditionDetail';
import { recordCastingView, getCastingViews } from '../../api/castingApi';
import { getMyApplications } from '../../api/applicationApi';
import { getMyTalentProfile } from '../../api/talentApi';
import { useAuth } from '../../contexts/AuthContext';
import RoleOnlyModal from '../../shared/components/RoleOnlyModal';
import { isDeadlineExpired } from '../../utils/castingDeadline';

function AuditionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { role, isLoggedIn, isTalent, isProducer, switchRole } = useAuth();
  const backLabel = location.state?.backLabel || '공고 목록으로';
  const [activeTab, setActiveTab] = useState('detail');

  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [applyInitialRoleId, setApplyInitialRoleId] = useState(null);
  // 로컬스토리지에서 지원 여부 즉시 복원 (페이지 재진입 시 깜빡임 방지)
  // 로컬스토리지 캐시로 초기값 설정 (API 응답 전 깜빡임 방지)
  // appliedRoleIds: 이 공고에서 이미 지원한 배역 id Set
  const [appliedRoleIds, setAppliedRoleIds] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem('applied-roles') || '{}');
      return new Set(cached[String(id)] ?? []);
    } catch { return new Set(); }
  });
  const [isCheckingApplied, setIsCheckingApplied] = useState(role === 'TALENT');
  const [viewCount, setViewCount] = useState(null);
  const [showTalentOnlyAlert, setShowTalentOnlyAlert] = useState(false);
  const [ageAlert, setAgeAlert] = useState(null);
  const [myTalentInfo, setMyTalentInfo] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);

  const { data, isLoading, isError, isNotFound, retry } = useAuditionDetail(id);

  // data.hasApplied는 항상 false로 고정되어 있어 사용하지 않음

  // 실제 지원 여부를 API로 확인 (서버 기준이 진실)
  useEffect(() => {
    if (!id || !role || role !== 'TALENT') {
      setIsCheckingApplied(false);
      return;
    }
    setIsCheckingApplied(true);
    getMyApplications()
      .then(apps => {
        const list = Array.isArray(apps) ? apps : (apps?.content ?? []);
        // 이 공고에 지원한 배역 id 목록 수집
        const roleIds = list
          .filter(a => String(a.castingPostId) === String(id))
          .map(a => String(a.castingRoleId))
          .filter(Boolean);
        const roleSet = new Set(roleIds);
        setAppliedRoleIds(roleSet);
        // 로컬스토리지 동기화
        try {
          const cached = JSON.parse(localStorage.getItem('applied-roles') || '{}');
          cached[String(id)] = [...roleSet];
          localStorage.setItem('applied-roles', JSON.stringify(cached));
        } catch {}
      })
      .catch(() => {})
      .finally(() => setIsCheckingApplied(false));
    if (role === 'TALENT') {
      getMyTalentProfile()
        .then(p => {
          const age = p.birthDate
            ? new Date().getFullYear() - new Date(p.birthDate).getFullYear() + 1
            : null;
          const gender = p.gender || null;
          setMyTalentInfo({ age, gender });
        })
        .catch(() => {});
    }
  }, [id, role]);

  useEffect(() => {
    if (!id) return;
    getCastingViews(id).then(res => setViewCount(res?.views ?? 0)).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!id) return;
    recordCastingView(id).catch(() => {});
  }, [id]);

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/castings');
  };

  const checkRoleConditions = (roleId) => {
    if (!myTalentInfo || !data) return true;
    const roleData = data.roles.find(r => String(r.id) === String(roleId));
    if (!roleData) return true;
    const { gender } = myTalentInfo;
    // 성별 조건만 체크 (나이 조건은 서버에서 처리하지 않으므로 프론트에서도 막지 않음)
    if (gender && roleData.gender && roleData.gender !== '무관' && roleData.gender !== 'ANY') {
      const genderMap = { MALE: '남성', FEMALE: '여성' };
      const myGenderKo = genderMap[gender] || gender;
      const roleGenderKo = genderMap[roleData.gender] || roleData.gender;
      if (roleData.gender !== myGenderKo && roleGenderKo !== myGenderKo) {
        setAgeAlert({
          title: '지원 불가',
          message: `이 배역은 ${roleGenderKo}만 지원 가능합니다.`,
        });
        return false;
      }
    }
    return true;
  };

  const handleRoleApply = (roleId) => {
    if (role !== 'TALENT') { setShowTalentOnlyAlert(true); return; }
    if (isCheckingApplied) return;
    if (appliedRoleIds.has(String(roleId))) return; // 해당 배역 이미 지원
    if (!checkRoleConditions(roleId)) return;
    setApplyInitialRoleId(roleId);
    setIsApplyModalOpen(true);
  };

  const handleRoleDetail = (role) => {
    setSelectedRole(role);
  };

  const isExpired = data?.status === 'closed' ||
    (data?.deadline && data.deadline !== '미정' ? isDeadlineExpired(data.deadline) : false);

  const handleSidebarApply = () => {
    if (role !== 'TALENT') { setShowTalentOnlyAlert(true); return; }
    if (isCheckingApplied || isExpired) return;
    setApplyInitialRoleId(null);
    setIsApplyModalOpen(true);
  };

  const handleApplySuccess = (appliedRoleId) => {
    setIsApplyModalOpen(false);
    if (appliedRoleId) {
      setAppliedRoleIds(prev => {
        const next = new Set(prev);
        next.add(String(appliedRoleId));
        try {
          const cached = JSON.parse(localStorage.getItem('applied-roles') || '{}');
          cached[String(id)] = [...next];
          localStorage.setItem('applied-roles', JSON.stringify(cached));
        } catch {}
        return next;
      });
    }
  };

  return (
    <>
    <PageLayout>
      <div className={styles.container}>
        {isLoading && (
          <div className={styles.stateBox} style={{ display: 'flex', justifyContent: 'center' }}>
            <LoadingSpinner size="lg" />
          </div>
        )}

        {isError && (
          <div className={styles.stateBox}>
            <p className={styles.stateTitle}>공고를 불러오지 못했습니다.</p>
            <div className={styles.stateActions}>
              <button className={styles.actionButton} onClick={retry}>다시 시도</button>
              <button className={styles.actionButton} onClick={() => navigate('/castings')}>목록으로</button>
            </div>
          </div>
        )}

        {isNotFound && (
          <div className={styles.stateBox}>
            <p className={styles.stateTitle}>공고를 찾을 수 없습니다.</p>
            <p className={styles.stateText}>삭제되었거나 존재하지 않는 공고입니다.</p>
            <button className={styles.actionButton} onClick={() => navigate('/castings')}>
              목록으로 돌아가기
            </button>
          </div>
        )}

        {!isLoading && !isError && !isNotFound && data && (
          <>
            <AuditionDetailBack onClick={handleBack} label={backLabel} />

            <div className={styles.bodyLayout}>
              {/* 왼쪽: 히어로 + 탭 + 콘텐츠 */}
              <div className={styles.leftColumn}>
                <AuditionDetailHero
                  category={data.category}
                  isNew={data.isNew}
                  status={data.status}
                  isClosing={data.isClosing}
                  title={data.title}
                  companyId={data.company.id}
                  companyName={data.company.name}
                  castingDirectorName={data.company.castingDirectorName}
                  isVerified={data.company.isVerified}
                />
                <AuditionDetailTabs activeTab={activeTab} onTabChange={setActiveTab} />
                <AuditionDetailContent
                  activeTab={activeTab}
                  data={data}
                  onRoleApply={handleRoleApply}
                  appliedRoleIds={isCheckingApplied ? null : appliedRoleIds}
                  isExpired={isExpired}
                  onRoleDetail={handleRoleDetail}
                />
              </div>

              {/* 오른쪽: sticky 사이드바 — 모든 배역에 지원 완료 시 버튼 비활성화 */}
              <AuditionDetailSidebar
                data={{ ...data, viewCount: viewCount ?? data.viewCount ?? 0 }}
                hasApplied={!isCheckingApplied && data.roles.length > 0 && data.roles.every(r => appliedRoleIds.has(String(r.id)))}
                isExpired={isExpired}
                onApply={handleSidebarApply}
              />
            </div>

            <ApplyModal
              isOpen={isApplyModalOpen}
              onClose={() => setIsApplyModalOpen(false)}
              auditionTitle={data.title}
              roles={data.roles}
              initialRoleId={applyInitialRoleId}
              auditionId={id}
              onSuccess={handleApplySuccess}
              myTalentInfo={myTalentInfo}
            />
            <RoleDetailModal
              isOpen={Boolean(selectedRole)}
              role={selectedRole}
              auditionTitle={data.title}
              appliedRoleIds={isCheckingApplied ? null : appliedRoleIds}
              isCheckingApplied={isCheckingApplied}
              isExpired={isExpired}
              onClose={() => setSelectedRole(null)}
              onApply={handleRoleApply}
            />
          </>
        )}
      </div>
    </PageLayout>

    {showTalentOnlyAlert && (
      <RoleOnlyModal
        title="배우 전용 기능입니다"
        message={isLoggedIn ? '공고 지원은 배우 계정에서만 이용할 수 있습니다.' : '배우로 로그인해서 이용해주세요.'}
        onClose={() => setShowTalentOnlyAlert(false)}
        hasBothRoles={isLoggedIn && isTalent && isProducer}
        onSwitch={() => { switchRole('TALENT'); window.location.href = '/talent/dashboard'; }}
        switchLabel="배우로 계정 전환하기"
        onboardingPath={isLoggedIn && !isTalent ? '/onboarding/talent' : undefined}
        onboardingLabel="배우 계정 만들러 가기"
      />
    )}
    {ageAlert && (
      <RoleOnlyModal
        title={ageAlert.title}
        message={ageAlert.message}
        onClose={() => setAgeAlert(null)}
      />
    )}
    </>
  );
}

export default AuditionDetailPage;
